import { useEffect, useState, useCallback } from "react";
import { useSubscriber } from "../../../../hooks";

// const BASE_URL = `http://localhost:3000/new-features`;
const BASE_URL = `${process.env.REACT_APP_SERVER_URL}/new-features`;

const useNewFeatureUpdate = () => {
  const [featureUpdates, setFeatureUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { fetchUserProfile, userDetails } = useSubscriber();
  const token = JSON.parse(localStorage.getItem("token"));

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Token ${token}`,
  };

  const fetchFeatureUpdates = useCallback(async () => {
    if (!userDetails?.msisdn) return;

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}?msisdn=${userDetails.msisdn}`, {
        headers,
      });
      if (!response.ok) throw new Error("Failed to fetch feature updates");
      const data = await response.json();
      setFeatureUpdates(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [userDetails?.msisdn]);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (userDetails?.msisdn) fetchFeatureUpdates();
  }, [fetchFeatureUpdates, userDetails?.msisdn]);

  const markAsViewed = async (id) => {
    if (!userDetails?.msisdn) return;
    try {
      const response = await fetch(`${BASE_URL}/${id}/viewed`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ msisdn: userDetails.msisdn }),
      });

      if (!response.ok)
        throw new Error("Failed to mark feature update as viewed");

      await fetchFeatureUpdates();
    } catch (error) {
      setError(error.message);
    }
  };

  const deleteFeatureUpdate = async (id) => {
    if (!userDetails?.msisdn) return;
    try {
      const response = await fetch(`${BASE_URL}/${id}`, {
        method: "DELETE",
        headers,
        body: JSON.stringify({ msisdn: userDetails.msisdn }),
      });

      if (!response.ok) throw new Error("Failed to delete feature update");

      await fetchFeatureUpdates();
    } catch (error) {
      setError(error.message);
    }
  };

  const deleteSelectedFeatureUpdates = async (ids) => {
    if (!userDetails?.msisdn) return;
    try {
      const response = await fetch(`${BASE_URL}/delete-selected`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ids, msisdn: userDetails.msisdn }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete selected feature updates");
      }

      const result = await response.json();
      await fetchFeatureUpdates();
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const markSelectedAsViewed = async (ids) => {
    if (!userDetails?.msisdn) return;
    try {
      const response = await fetch(`${BASE_URL}/mark-selected-viewed`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ ids, msisdn: userDetails.msisdn }),
      });

      if (!response.ok) {
        throw new Error("Failed to mark selected feature updates as viewed");
      }

      const result = await response.json();
      await fetchFeatureUpdates();
      return result;
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    }
  };

  const notifications = featureUpdates?.data
    ? featureUpdates.data.map((item) => ({
        id: item._id,
        featureTitle: item.featureTitle,
        featureMessage: item.featureMessage,
        isViewed: item.isViewed,
        createdAt: item.createdAt,
        featureButtonText: item.featureButtonText,
        featureButtonUrl: item.featureButtonUrl,
        releaseDate: item.releaseDate,
        releaseTime: item.releaseTime,
      }))
    : [];

  const newFeatureUpdates = featureUpdates?.data || [];

  const unreadCount = Array.isArray(newFeatureUpdates)
    ? newFeatureUpdates.filter((update) => !update.isViewed).length
    : 0;

  return {
    loading,
    error,
    deleteFeatureUpdate,
    markAsViewed,
    deleteSelectedFeatureUpdates,
    markSelectedAsViewed,
    notifications,
    unreadCount,
  };
};

export default useNewFeatureUpdate;
